import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CareJourneySummary, MoodLevel, UserBadgeSummary } from '@lighthouse/shared';
import { api } from '../services/api';

interface Affirmation {
  id: string;
  text: string;
  author: string | null;
  language: string;
}

interface StreakStatus {
  currentStreak: number;
  bestStreak: number;
  lastCheckInDate: string | null;
  checkedInToday: boolean;
  alreadyCheckedIn?: boolean;
}

export function useCareJourney(enabled = true) {
  return useQuery({
    queryKey: ['care-journey'],
    queryFn: () => api.get<CareJourneySummary>('/engagement/journey'),
    enabled,
  });
}

export function useTodayMood(enabled = true) {
  return useQuery({
    queryKey: ['mood-today'],
    queryFn: () => api.get<{ mood: MoodLevel } | null>('/engagement/mood/today'),
    enabled,
  });
}

export function useLogMood() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { mood: MoodLevel; note?: string }) =>
      api.post('/engagement/mood', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mood-today'] });
      void qc.invalidateQueries({ queryKey: ['care-journey'] });
    },
  });
}

export function useStreak(enabled = true) {
  return useQuery({
    queryKey: ['streak'],
    queryFn: () => api.get<StreakStatus>('/engagement/streak'),
    enabled,
  });
}

export function useStreakCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<StreakStatus>('/engagement/streak/check-in', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['streak'] });
      void qc.invalidateQueries({ queryKey: ['care-journey'] });
    },
  });
}

export function useDailyAffirmation(language = 'en', enabled = true) {
  return useQuery({
    queryKey: ['affirmation-today', language],
    queryFn: () =>
      api.get<Affirmation | null>(`/engagement/affirmations/today?language=${language}`),
    enabled,
  });
}

export function useBadges(enabled = true) {
  return useQuery({
    queryKey: ['badges'],
    queryFn: () => api.get<UserBadgeSummary[]>('/engagement/badges'),
    enabled,
  });
}
